import { useEffect, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { EventCard } from '../components/predictions/EventCard'
import {
  getPresentationCatalogFeed,
  getPresentationHomeFeed,
  type PresentationCatalogFeed,
  type PresentationHomeFeed,
} from '../services/presentationService'
import { categoryLabel, useLocale } from '../i18n/locale'
import type { PredictionCategory } from '../types/prediction'
import type { CurrentFreshness } from '../types/freshness'
import type { PredictionViewModel, PresentationSourceStatus } from '../types/presentation'
import { formatUpdatedAt } from '../utils/format'
import { freshnessStatusLabel } from '../utils/freshness'

const categoryKeys: PredictionCategory[] = ['finance', 'technology', 'world', 'sports', 'entertainment', 'crypto']
type PageFeed = PresentationHomeFeed | PresentationCatalogFeed

function scrollToHashTarget(hash: string) {
  const encodedId = hash.replace(/^#/, '')
  if (!encodedId) return

  let targetId: string
  try {
    targetId = decodeURIComponent(encodedId)
  } catch {
    return
  }

  const target = document.getElementById(targetId)
  if (!target) return

  const header = document.querySelector<HTMLElement>('.site-header')
  const headerPosition = header ? window.getComputedStyle(header).position : 'static'
  const headerOffset = header && ['fixed', 'sticky'].includes(headerPosition)
    ? header.getBoundingClientRect().height + 12
    : 12
  const top = target.getBoundingClientRect().top + window.scrollY - headerOffset
  window.scrollTo({ top: Math.max(0, top), behavior: 'auto' })
}

interface HomePageProps {
  categoryOverride?: PredictionCategory
}
function freshnessTimestamp(feed: PresentationHomeFeed | PresentationCatalogFeed) {
  return feed.freshness.dataset.presentationGeneratedAt
    ?? feed.freshness.dataset.eventGeneratedAt
    ?? feed.freshness.dataset.catalogGeneratedAt
}

function EventGrid({ items, variant = 'compact' }: { items: PredictionViewModel[]; variant?: 'feature' | 'spotlight' | 'movement' | 'compact' }) {
  const { t } = useLocale()
  if (items.length === 0) return <div className="inline-empty-state">{t('home.noData')}</div>

  return (
    <div className={`presentation-grid presentation-grid-${variant}`}>
      {items.map((item, index) => (
        <EventCard
          key={item.eventId}
          prediction={item}
          variant={variant === 'feature' ? index === 0 ? 'feature' : 'spotlight' : variant}
        />
      ))}
    </div>
  )
}

function SourceStrip({ sources, freshness }: { sources: PresentationSourceStatus[]; freshness: CurrentFreshness }) {
  const { locale, t } = useLocale()
  const updatedAt = freshness.dataset.presentationGeneratedAt ?? freshness.dataset.eventGeneratedAt ?? freshness.dataset.catalogGeneratedAt
  return (
    <section className="home-source-strip" aria-label={t('home.catalogEyebrow')}>
      <div>
        <span className="presentation-label">{t('home.catalogEyebrow')}</span>
        <strong>{t('home.recentlySynced')}</strong>
        <small>
          {t('common.dataUpdated', { time: formatUpdatedAt(updatedAt, false, locale) })}
          {' · '}{freshnessStatusLabel(freshness.current.status, locale)}
        </small>
      </div>
      <div className="home-source-list">
        {sources.map((source) => (
          <span className={`home-source-item home-source-item-${source.status}`} key={source.name}>
            <span className="status-dot" aria-hidden="true" />
            <b>{source.name}</b>
            <em>{t('common.synced')}</em>
            <small>{source.status === 'disabled' ? t('common.hidden') : source.detail}</small>
          </span>
        ))}
      </div>
    </section>
  )
}

export function HomePage({ categoryOverride }: HomePageProps) {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { locale, t } = useLocale()
  const [feed, setFeed] = useState<PageFeed | null>(null)
  const [loadedFeedKey, setLoadedFeedKey] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [requestError, setRequestError] = useState<string | null>(null)
  const searchQuery = searchParams.get('q')?.trim() ?? ''
  const searchCategory = searchParams.get('category')
  const activeCategory = categoryOverride
    ?? (searchCategory && categoryKeys.includes(searchCategory as PredictionCategory) ? searchCategory as PredictionCategory : null)
  const isExplore = Boolean(activeCategory || searchQuery)
  const feedKey = `${locale}:${isExplore ? 'catalog' : 'home'}:${activeCategory ?? ''}:${searchQuery}`

  async function loadFeed(forceRefresh = false, page = 0, append = false) {
    setLoading(true)
    setRequestError(null)
    try {
      if (isExplore) {
        const nextFeed = await getPresentationCatalogFeed({
          forceRefresh,
          category: activeCategory,
          query: searchQuery,
          page,
          locale,
        })
        setFeed((current) => {
          if (!append || !current || current.kind !== 'catalog') return nextFeed
          const existingIds = new Set(current.results.map((item) => item.eventId))
          const added = nextFeed.results.filter((item) => !existingIds.has(item.eventId))
          return { ...nextFeed, results: [...current.results, ...added], total: current.total + added.length }
        })
        setLoadedFeedKey(feedKey)
      } else {
        setFeed(await getPresentationHomeFeed({ forceRefresh, locale }))
        setLoadedFeedKey(feedKey)
      }
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : t('home.errorTitle'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadFeed()
  }, [feedKey])

  useEffect(() => {
    if (feed && loadedFeedKey === feedKey) {
      window.dispatchEvent(new CustomEvent('yujian:feed-update', { detail: { at: feed.fetchedAt } }))
    }
  }, [feed, feedKey, loadedFeedKey])

  const visibleFeed = loadedFeedKey === feedKey ? feed : null
  const homeFeed = visibleFeed?.kind === 'home' ? visibleFeed : null
  const catalogFeed = visibleFeed?.kind === 'catalog' ? visibleFeed : null
  const title = searchQuery ? `${t('home.searchResults')}: ${searchQuery}` : activeCategory ? categoryLabel(activeCategory, locale) : t('home.todayTitle')

  useEffect(() => {
    if (!location.hash || !visibleFeed) return

    const frame = window.requestAnimationFrame(() => scrollToHashTarget(location.hash))
    return () => window.cancelAnimationFrame(frame)
  }, [location.hash, visibleFeed])

  return (
    <div className={`page-container home-page presentation-page ${isExplore ? 'presentation-explore-page' : ''}`}>
      {!isExplore ? (
        <section className="presentation-hero" aria-labelledby="board-title">
          <div>
            <p className="eyebrow">{t('home.todayEyebrow')}</p>
            <h1 id="board-title">{t('home.todayTitle')}</h1>
            <p className="board-description">{t('home.todayDescription')}</p>
          </div>
          <div className="presentation-hero-readout" aria-label={t('home.summaryAria')}>
            <strong>{homeFeed?.today.length ?? '—'}</strong>
            <span>{t('home.summaryCount')}</span>
            <small>
              {homeFeed
                ? `${t('common.dataUpdated', { time: formatUpdatedAt(freshnessTimestamp(homeFeed), false, locale) })} · ${freshnessStatusLabel(homeFeed.freshness.current.status, locale)}`
                : t('home.readingData')}
            </small>
          </div>
        </section>
      ) : (
        <section className="presentation-hero presentation-explore-hero" aria-labelledby="explore-title">
          <div>
            <p className="eyebrow">{t('home.browseEyebrow', { mode: activeCategory ? t('home.categoryMode') : t('home.searchMode') })}</p>
            <h1 id="explore-title">{title}</h1>
            <p className="board-description">{t('home.browseDescription')}</p>
          </div>
          <Link className="button button-secondary" to="/">{t('home.backToday')}</Link>
        </section>
      )}

      {loading && !visibleFeed && (
        <div className="feed-state-panel" role="status" aria-live="polite">
          <p className="eyebrow">{t('home.loadingEyebrow')}</p>
          <h2>{t('home.loadingTitle')}</h2>
          <p>{t('home.loadingBody')}</p>
        </div>
      )}

      {requestError && !visibleFeed && (
        <div className="feed-state-panel feed-state-panel-error" role="alert">
          <p className="eyebrow">{t('home.errorEyebrow')}</p>
          <h2>{t('home.errorTitle')}</h2>
          <p>{requestError}</p>
          <button className="button button-secondary" type="button" onClick={() => void loadFeed(true)}>{t('home.retry')}</button>
        </div>
      )}

      {homeFeed && (
        <>
          <section className="board-section presentation-section today-section" id="today" aria-labelledby="today-title">
            <div className="section-heading">
              <div><p className="eyebrow">{t('home.todaySectionEyebrow')}</p><h2 id="today-title">{t('home.todaySectionTitle')}</h2></div>
              <span className="section-note">{t('home.todaySectionNote', { count: homeFeed.today.length })}</span>
            </div>
            <EventGrid items={homeFeed.today.slice(0, 6)} variant="feature" />
            {homeFeed.today.length > 6 && <div className="presentation-more-grid"><span className="presentation-more-label">{t('home.continueBrowsing')}</span><EventGrid items={homeFeed.today.slice(6)} variant="compact" /></div>}
          </section>

          <section className="board-section presentation-section changing-section" id="changing" aria-labelledby="changing-title">
            <div className="section-heading"><div><p className="eyebrow">{t('home.changingEyebrow')}</p><h2 id="changing-title">{t('home.changingTitle')}</h2></div><span className="section-note">{t('home.changingNote', { count: homeFeed.changing.length })}</span></div>
            <EventGrid items={homeFeed.changing} variant="movement" />
          </section>

          <section className="board-section presentation-section watch-section" id="watch" aria-labelledby="watch-title">
            <div className="section-heading"><div><p className="eyebrow">{t('home.watchEyebrow')}</p><h2 id="watch-title">{t('home.watchTitle')}</h2></div><span className="section-note">{t('home.watchNote', { count: homeFeed.watch.length })}</span></div>
            <EventGrid items={homeFeed.watch} variant="compact" />
          </section>

          {homeFeed.comparisons.length > 0 && <section className="board-section presentation-section comparison-section" id="comparison" aria-labelledby="comparison-title">
            <div className="section-heading"><div><p className="eyebrow">{t('home.comparisonsEyebrow')}</p><h2 id="comparison-title">{t('home.comparisonsTitle')}</h2></div><span className="section-note">{t('home.comparisonsNote')}</span></div>
            <EventGrid items={homeFeed.comparisons} variant="compact" />
          </section>}

          <SourceStrip sources={homeFeed.sources} freshness={homeFeed.freshness} />
        </>
      )}

      {catalogFeed && <>
        <section className="board-section presentation-section catalog-section" id="results" aria-labelledby="results-title">
          <div className="section-heading"><div><p className="eyebrow">{t('home.catalogEyebrow')}</p><h2 id="results-title">{searchQuery ? t('home.searchResults') : t('home.categoryPredictions', { category: categoryLabel(activeCategory ?? 'world', locale) })}</h2></div><span className="section-note">{t('home.displayCount', { count: catalogFeed.results.length })}</span></div>
          {catalogFeed.results.length > 0 ? <EventGrid items={catalogFeed.results} variant="compact" /> : <div className="inline-empty-state">{t('home.noMatches')}</div>}
          {catalogFeed.hasMore && <button className="catalog-load-more" type="button" onClick={() => void loadFeed(false, catalogFeed.nextPage, true)} disabled={loading}>{loading ? t('home.loadMoreLoading') : t('home.loadMore')}<span aria-hidden="true">↘</span></button>}
        </section>
        <SourceStrip sources={catalogFeed.sources} freshness={catalogFeed.freshness} />
      </>}

      <div className="board-tail presentation-tail"><span>{t('home.tailDisclaimer')}</span><span>{t('home.tailSource')}</span></div>
    </div>
  )
}
